import { memo, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, EyeOff, Newspaper, RefreshCw, FileText, Activity, Sparkles, FileSearch } from 'lucide-react';
import Button from '../../ui/Button';
import { BusinessInsights } from './BusinessInsights';
import { ExecOverview } from './ExecOverview';
import SmartLoader from '../../ui/SmartLoader';
import type { SmartLoaderPhase } from '../../ui/SmartLoader';
import { NewsSummaryHeader } from './NewsSummaryHeader';
import { RisksOpportunities } from './RisksOpportunities';
import { useCompanyIntelligenceBrief } from './useCompanyIntelligenceBrief';
import type { UseCompanyIntelligenceBriefOptions } from './useCompanyIntelligenceBrief';
import {
  PerArticleFilterBar,
  applyFiltersAndSort,
  DEFAULT_SORT,
  EMPTY_FILTERS,
  type ArticleFilters,
  type ArticleSort,
} from './PerArticleFilterBar';
import { SourcingInsightCard } from '../sourcing-intelligence';
import type { SourcingArticleAnalysis } from '../sourcing-intelligence';

interface NewsIntelligenceTabProps {
  companyName: string;
  defaultDaysBack?: UseCompanyIntelligenceBriefOptions['defaultDaysBack'];
}

// The brief is a single opaque request with no backend progress, so these phases
// are cosmetic and time-driven (elapsed mode).
const NEWS_LOAD_PHASES: SmartLoaderPhase[] = [
  { id: 'fetch', label: 'Fetching recent coverage', hint: 'Gathering news across sources', icon: Newspaper, startSec: 0 },
  { id: 'read', label: 'Reading articles', hint: 'Parsing article content', icon: FileText, startSec: 4 },
  { id: 'sentiment', label: 'Analyzing sentiment', hint: 'Stance, confidence, drivers', icon: Activity, startSec: 14 },
  { id: 'signals', label: 'Extracting key signals', hint: 'Risks, opportunities, catalysts', icon: Sparkles, startSec: 28 },
  { id: 'summarize', label: 'Summarizing insights', hint: 'Executive brief', icon: FileSearch, startSec: 40 },
];
const NEWS_EXPECTED_SEC = 48;

function EmptyState({ onRefresh }: { onRefresh: () => void }) {
  return (
    <div className="py-16 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-500">
        <Newspaper className="h-5 w-5" />
      </div>
      <p className="text-sm font-medium text-neutral-700">No news coverage found</p>
      <p className="mt-1 text-xs text-neutral-400">
        Try widening the time range or check back later.
      </p>
      <Button variant="outline" size="sm" onClick={onRefresh} className="mt-4">
        <RefreshCw className="h-4 w-4" />
        Try again
      </Button>
    </div>
  );
}

function ErrorState({ message, onRefresh }: { message: string; onRefresh: () => void }) {
  return (
    <div className="py-12 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-500">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <p className="text-sm font-medium text-red-700">{message}</p>
      <Button variant="outline" size="sm" onClick={onRefresh} className="mt-4">
        <RefreshCw className="h-4 w-4" />
        Retry
      </Button>
    </div>
  );
}

function SkippedArticlesGroup({ articles }: { articles: SourcingArticleAnalysis[] }) {
  const [expanded, setExpanded] = useState(false);
  if (articles.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50/40">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-100/60"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2.5">
          <EyeOff className="h-3.5 w-3.5 text-neutral-400" />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-neutral-500">
              Not relevant
            </p>
            <p className="mt-0.5 text-[11px] text-neutral-400">
              {articles.length} {articles.length === 1 ? 'article' : 'articles'} filtered out — no material connection
            </p>
          </div>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      {expanded && (
        <ul className="divide-y divide-neutral-100 border-t border-neutral-100 bg-white">
          {articles.map((a, i) => {
            const reason = (a.skip_reason ?? '').replace(/^SKIP:\s*/i, '').trim();
            return (
              <li
                key={a.article_url || `skipped-${i}`}
                className="grid grid-cols-1 gap-1 px-4 py-2.5 sm:grid-cols-[minmax(0,3fr)_minmax(0,4fr)] sm:items-baseline sm:gap-4"
              >
                <p className="truncate text-xs font-medium text-neutral-700" title={a.article_title}>
                  {a.article_title}
                </p>
                <p className="line-clamp-2 text-[11px] leading-relaxed text-neutral-400" title={reason}>
                  {reason || 'No reason provided'}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function NewsIntelligenceTabBase({
  companyName,
  defaultDaysBack = 90,
}: NewsIntelligenceTabProps) {
  // Single unified call — batch insights + per-article cards arrive together,
  // so there's no layout-shift gating to worry about anymore.
  const {
    data: brief,
    isLoading,
    isRefreshing,
    error,
    daysBack,
    setDaysBack,
    refresh,
    fetchedAt,
  } = useCompanyIntelligenceBrief({ companyName, defaultDaysBack });

  const data = brief?.news ?? null;
  const articles = data?.articles ?? [];
  const keyInsights = data?.key_insights ?? [];
  const risks = data?.risks ?? [];
  const articleInsightLinks = data?.article_insight_links ?? [];
  const articleRiskLinks = data?.article_risk_links ?? [];

  const { sourcingAnalyzed, sourcingSkipped } = useMemo(() => {
    const all = brief?.sourcing.analysis?.articles ?? [];
    return {
      sourcingAnalyzed: all.filter((a) => a.status === 'analyzed'),
      sourcingSkipped: all.filter((a) => a.status === 'skipped'),
    };
  }, [brief]);
  const sourcingBullish = brief?.sourcing.analysis?.direction_breakdown?.bullish ?? 0;
  const sourcingBearish = brief?.sourcing.analysis?.direction_breakdown?.bearish ?? 0;

  // Per-article filter + sort state. Reset whenever a new company is loaded
  // (the brief response carries a different article corpus).
  const [filters, setFilters] = useState<ArticleFilters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<ArticleSort>(DEFAULT_SORT);
  useEffect(() => {
    setFilters(EMPTY_FILTERS);
    setSort(DEFAULT_SORT);
  }, [companyName]);
  const visibleArticles = useMemo(
    () => applyFiltersAndSort(sourcingAnalyzed, filters, sort),
    [sourcingAnalyzed, filters, sort]
  );

  const anyLoading = isLoading;
  const isAnyRefreshing = isRefreshing;

  const showEmpty =
    !anyLoading &&
    !error &&
    !!data &&
    articles.length === 0 &&
    keyInsights.length === 0 &&
    risks.length === 0;

  const hasOpportunities = (data?.investment_interpretation?.bullish_factors?.length ?? 0) > 0;

  return (
    <div>
      <NewsSummaryHeader
        companyName={companyName}
        data={data}
        daysBack={daysBack}
        onDaysBackChange={setDaysBack}
        onRefresh={refresh}
        isLoading={anyLoading}
        isRefreshing={isAnyRefreshing}
        fetchedAt={fetchedAt}
      />

      <div className="pt-5">
        {error && <ErrorState message={error} onRefresh={refresh} />}
        {!error && anyLoading && (
          <SmartLoader
            title="Analyzing recent news"
            subtitle="This can take 1–2 minutes"
            phases={NEWS_LOAD_PHASES}
            progress={{ mode: 'elapsed', expectedSec: NEWS_EXPECTED_SEC }}
            active={anyLoading}
          />
        )}
        {!error && !anyLoading && showEmpty && <EmptyState onRefresh={refresh} />}

        {!error && !anyLoading && data && !showEmpty && (
          <div className="space-y-8">
            {/* 1. Executive overview — stance, confidence, summary, growth drivers / watch points */}
            <ExecOverview
              data={data}
              analyzedCount={sourcingAnalyzed.length}
              skippedCount={sourcingSkipped.length}
            />

            {/* 2. Key insights — bullish/bearish signal cards.
                `articles` is passed so each card can resolve its
                `article_insight_links` to real publisher pills. */}
            {keyInsights.length > 0 && (
              <BusinessInsights
                insights={keyInsights}
                articleInsightLinks={articleInsightLinks}
                articleRiskLinks={articleRiskLinks}
                articles={articles}
              />
            )}

            {/* 3. Risks & opportunities */}
            {(risks.length > 0 || hasOpportunities) && (
              <RisksOpportunities risks={risks} data={data} />
            )}

            {/* 4. Per-article financial signals (bullish/bearish per article) — canonical article view */}
            {(sourcingAnalyzed.length > 0 || sourcingSkipped.length > 0) && (
              <section className="space-y-4">
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <h2 className="text-sm font-bold uppercase tracking-widest text-neutral-800">
                      Per-Article Signals
                    </h2>
                    <p className="mt-1 text-xs text-neutral-500">
                      Equity-analyst classification of each article — direction, magnitude, time horizon, category
                    </p>
                  </div>

                  {sourcingAnalyzed.length > 0 && (() => {
                    const neutralCount = Math.max(
                      sourcingAnalyzed.length - sourcingBullish - sourcingBearish,
                      0,
                    );
                    return (
                      <div className="flex items-center gap-3 text-[11px] font-semibold text-neutral-500">
                        <span>{sourcingAnalyzed.length} analysed</span>
                        {sourcingBullish > 0 && (
                          <span className="inline-flex items-center gap-1 text-emerald-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            {sourcingBullish} bullish
                          </span>
                        )}
                        {sourcingBearish > 0 && (
                          <span className="inline-flex items-center gap-1 text-rose-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                            {sourcingBearish} bearish
                          </span>
                        )}
                        {neutralCount > 0 && (
                          <span className="inline-flex items-center gap-1 text-neutral-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" />
                            {neutralCount} neutral
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {sourcingAnalyzed.length > 0 && (
                  <>
                    <PerArticleFilterBar
                      filters={filters}
                      onFiltersChange={setFilters}
                      sort={sort}
                      onSortChange={setSort}
                      totalCount={sourcingAnalyzed.length}
                      filteredCount={visibleArticles.length}
                    />

                    {visibleArticles.length > 0 ? (
                      <div className="space-y-4">
                        {visibleArticles.map((article) => (
                          <SourcingInsightCard key={article.article_url} article={article} />
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-neutral-200 bg-white px-4 py-10 text-center">
                        <p className="text-sm font-medium text-neutral-700">
                          No articles match the current filters
                        </p>
                        <p className="mt-1 text-xs text-neutral-400">
                          Try widening the filters or hitting Reset.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {sourcingSkipped.length > 0 && (
                  <SkippedArticlesGroup articles={sourcingSkipped} />
                )}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const NewsIntelligenceTab = memo(NewsIntelligenceTabBase);